import { compare, money, multiplyByRatio, type Money } from "@stb/core/money"
import {
  INCOME_TAX_YEARS,
  incomeTaxYear,
  type Bracket,
  type IncomeTaxYear,
  type PayPeriod,
  type Residency,
  type ShownPeriod,
  type StudyLoanBand,
  type TakeHomeResult,
} from "@stb/jurisdiction-au/income-tax"
import {
  ChevronDown,
  CircleDollarSign,
  Copy,
  GraduationCap,
  HeartPulse,
  Info,
  Keyboard,
  Landmark,
  Percent,
  PiggyBank,
  Sparkles,
  TrendingUp,
  Wallet,
  type LucideIcon,
} from "lucide-react"
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react"

import type { Locale } from "@/core/i18n/locale"
import { useLocale } from "@/core/i18n/locale-context"
import { Badge } from "@/ui/badge"
import { Button } from "@/ui/button"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"

import { PAY_COPY_ZH } from "../../content/zh/pay-calculator-copy"
import { displayYear, formatCheckedOn } from "../home/home-content"
import { formatAud } from "./calculator"
import {
  BracketLadder,
  ChartPatterns,
  FillKeySwatch,
  IconChip,
  MedicareShadeChart,
  PaySplitBar,
  TaxCutBars,
  type ChipTone,
  type FillKey,
} from "./pay-calculator-charts"
import {
  amountStepCents,
  bandRange,
  bracketPosition,
  bracketCaption,
  bracketTable,
  caretAfter,
  evaluate,
  formToParams,
  groupAmount,
  normalizeAmountInput,
  parseAmount,
  parseForm,
  payRise,
  payShares,
  riseCapDollars,
  SHOWN_PERIODS,
  stepAmount,
  studyLoanRule,
  taxCut,
  VARIANT_DEFAULTS,
  type PayForm,
  type PayVariant,
} from "./pay-calculator-model"
import { PROVISIONAL_TEXT } from "./provisional-text"
import { SimpleTable } from "./simple-table"
import { SourceLine } from "./source-line"
import {
  toolHeroBox,
  toolHeroInput,
  toolHeroPrefix,
  toolInputPanel,
  toolPanelFoot,
  toolPanelHeading,
  toolPanels,
  toolPeek,
  toolResultFigure,
  toolResultPanel,
  toolResultSlot,
} from "./tool-panels"

const ATO_ESTIMATOR_URL = "https://www.ato.gov.au/calculators-and-tools/income-tax-estimator"
const MEDICARE_EXEMPTION_URL =
  "https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy/medicare-levy-exemption"
export { PROVISIONAL_TEXT }
export const ATO_MEDICARE_CALCULATOR_URL =
  "https://www.ato.gov.au/calculators-and-tools/tax-return-medicare-levy"

/** The icon each page puts in front of its hero figure (T917); the text comes from the copy. */
const EMPHASIS = {
  pay: { key: "takeHome", icon: Wallet },
  help: { key: "studyLoanRepayment", icon: GraduationCap },
  medicare: { key: "medicareLevy", icon: HeartPulse },
} as const

/** Chip order follows the design: biggest period first. */
const PERIOD_CHIPS: readonly PayPeriod[] = ["year", "month", "fortnight", "week", "day", "hour"]

/** Which ATO source a Working step cites (the zh copy has its own label per kind; the link and date come from the rule). */
export type SourceKind =
  | "super"
  | "deduction"
  | "resident"
  | "foreign"
  | "working-holiday"
  | "lito"
  | "medicare"
  | "help"

/** Text around emphasised values: [before, between, after]. */
type Pieces = readonly [string, string, string]
type PiecesTwo = readonly [string, string]

/** All text of the calculator in one language. English is the source; the Chinese copy lives in content/zh/pay-calculator-copy.ts. */
export interface PayCopy {
  emphasis: Record<PayVariant, { title: string; label: string; short: string }>
  periods: Record<PayPeriod, string>
  shown: Record<ShownPeriod, string>
  /** "Take-home per fortnight" */
  per: (what: string, show: ShownPeriod) => string
  /** "a fortnight" */
  aShown: (show: ShownPeriod) => string
  sourceLabel: (kind: SourceKind, engineLabel: string) => string
  checkedOn: (iso: string) => string
  shortcuts: {
    button: string
    title: string
    /** In the order of SHORTCUT_KEYS; the first is replaced by `changeBy`. */
    actions: readonly string[]
    changeBy: (amount: string) => string
    only: string
    hintAmount: string
    hintPeriod: string
    hintAll: string
  }
  hintChangeBy: (amount: string) => string
  yourPay: string
  payBeforeTax: string
  yearlyGross: (amount: string) => string
  paidPer: string
  quickSalaries: string
  presetChip: (thousands: number) => string
  superLegend: string
  superExcl: string
  superIncl: string
  incomeYear: string
  yearOption: (fy: string) => string
  hoursLabel: string
  daysLabel: string
  moreOptions: string
  moreHint: { help: string; other: string }
  helpTitle: string
  helpHint: string
  residencyLabel: string
  residency: Record<Residency, string>
  otherLabel: string
  otherHint: string
  medicareTitle: (rate: string) => string
  medicareHint: string
  deductionTitle: (max: string) => string
  deductionHint: (fy: string) => string
  riseQuestion: string
  riseValue: (amount: string) => string
  riseValueText: (amount: string) => string
  riseNoText: string
  /** [before the take-home figure, between it and the rise, after] */
  riseLine: (show: ShownPeriod) => Pieces
  riseKeep: (cents: number, help: boolean) => string
  riseEmpty: string
  riseSaid: (takeHome: string, rise: string, show: ShownPeriod) => string
  showPer: string
  figureCaption: (label: string, fromThisJob: boolean, show: ShownPeriod) => string
  rates: Pieces
  ratesEmpty: (dash: string) => string
  payBeforeTaxShort: string
  superOnTop: string
  youKeep: (share: string) => string
  splitRight: (pct: string, helpOn: boolean) => string
  whereGoes: (show: ShownPeriod) => string
  rowTax: string
  rowMedicare: string
  rowHelp: string
  rowHelpOff: string
  rowTakeHome: string
  rowSuper: (rate: string) => string
  medicareSingle: (v: { lower: string; shade: string; upper: string; rate: string }) => string
  medicareOff: string
  medicareOffSource: string
  deductionIncluded: (amount: string) => string
  provisional: string
  provisionalFallback: string
  copyLink: string
  localNote: string
  copied: string
  copyFailed: string
  chartsLabel: string
  bracket: {
    title: string
    marginal: string
    plusLevy: (rate: string) => string
    axis: string
    invalid: string
    columns: [string, string, string]
    tableCaption: (taxable: string | undefined, fy: string) => string
  }
  cut: {
    title: (fy: string) => string
    plain: string
    line: (more: boolean, perShown: string, show: ShownPeriod) => string
    keyTax: string
    keySave: string
    comes: (amount: string) => string
    rateChanges: string
    rateChange: (from: string, to: string) => string
    deduction: (max: string) => string
    showTable: string
    noPrevious: (fy: string) => string
    invalid: string
    columns: [string, string, string, string]
    tableCaption: (gross: string | undefined, previous: string, current: string) => string
  }
  disclaimer: { lead: string; estimator: string; medicare: string; medicareLink: string }
  how: {
    summary: string
    sub: (fy: string) => string
    enter: string
    payslip: string
  }
  working: {
    pay: (gross: string, rate: string, employerSuper: string) => string
    deduction: (notApplied: boolean, amount: string, taxable: string) => string
    tax: (v: { base: string; rate: string; taxable: string; from: string; tax: string }) => string
    lito: (amount: string) => string
    medicareNone: string
    medicareOff: string
    medicare: (rate: string, amount: string) => string
    help: (amount: string) => string
    takeHome: (v: {
      gross: string
      tax: string
      lito: string
      levy: string
      loan: string
      marginal: string
    }) => PiecesTwo
  }
  help: {
    title: string
    wholeIncome: string
    overBand: string
    axis: string
    turnOn: string
    combined: (taxable: string, repayment: string) => string
    under: (taxable: string) => string
    repay: (taxable: string, repayment: string) => string
    invalid: string
    tableCaption: (fy: string) => string
    columns: [string, string, string]
    here: string
  }
  medicareFigure: {
    title: string
    yearSingle: string
    axis: string
    invalid: string
    noLevyShown: string
    combined: (taxable: string) => string
    atOrUnder: (lower: string) => string
    reduction: (rate: string) => string
    full: (upper: string, rate: string) => string
    tableCaption: (fy: string) => string
    columns: [string, string]
    upTo: (lower: string) => string
    nil: string
    shade: (shade: string, lower: string) => string
    over: (upper: string) => string
    fullRate: (rate: string) => string
  }
  showTable: string
}

const PAY_COPY_EN: PayCopy = {
  emphasis: {
    pay: { title: "Your take-home pay", label: "Take-home pay", short: "Take-home" },
    help: { title: "Your HELP repayment", label: "HELP repayment", short: "HELP repayment" },
    medicare: { title: "Your Medicare levy", label: "Medicare levy", short: "Medicare levy" },
  },
  periods: {
    year: "Year",
    month: "Month",
    fortnight: "Fortnight",
    week: "Week",
    day: "Day",
    hour: "Hour",
  },
  shown: { week: "Week", fortnight: "Fortnight", month: "Month", year: "Year" },
  per: (what, show) => `${what} per ${show}`,
  aShown: (show) => `a ${show}`,
  sourceLabel: (_kind, engineLabel) => engineLabel,
  checkedOn: formatCheckedOn,
  shortcuts: {
    button: "Keyboard shortcuts",
    title: "Keyboard shortcuts",
    actions: [
      "Change the amount by one step",
      "Ten steps",
      "A tenth of a step",
      "Update the result now",
      "Move between pay periods",
      "Show these shortcuts",
      "Close this list",
    ],
    changeBy: (amount) => `Change the amount by ${amount}`,
    only: "Only while the calculator has focus.",
    hintAmount: "amount",
    hintPeriod: "period",
    hintAll: "all shortcuts",
  },
  hintChangeBy: (amount) => `change by ${amount}`,
  yourPay: "Your pay",
  payBeforeTax: "Pay before tax",
  yearlyGross: (amount) => `${amount} a year before tax.`,
  paidPer: "Paid per",
  quickSalaries: "Quick salaries per year",
  presetChip: (k) => `A$${k}k`,
  superLegend: "Super",
  superExcl: "Not included",
  superIncl: "Included",
  incomeYear: "Income year",
  yearOption: (fy) =>
    `${displayYear(fy)} (1 Jul ${fy.slice(0, 4)} – 30 Jun ${Number(fy.slice(0, 4)) + 1})`,
  hoursLabel: "Hours a week",
  daysLabel: "Days a week",
  moreOptions: "More options",
  moreHint: {
    help: "residency, second job",
    other: "HELP debt, residency, second job",
  },
  helpTitle: "I have a HELP or other study loan",
  helpHint: "Adds the compulsory repayment",
  residencyLabel: "Tax residency",
  residency: {
    resident: "Australian resident",
    foreign: "Foreign resident",
    "working-holiday": "Working holiday maker",
  },
  otherLabel: "Income from another job (per year)",
  otherHint: "That job uses your tax-free threshold, so this one is taxed at your top rate.",
  medicareTitle: (rate) => `Include the ${rate} Medicare levy`,
  medicareHint:
    "Untick if you have a Medicare levy exemption, for example a medical exemption or not being entitled to Medicare benefits. Foreign residents for the whole year pay no levy.",
  deductionTitle: (max) => `Include the ${max} standard deduction (off by default)`,
  deductionHint: (fy) =>
    `The ATO applies it automatically in your ${fy} return, for residents, on wages, salary and similar pay with tax withheld. It is cut by any work expenses you claim, and it does not apply to business income such as sole trader profit. Tick to include it.`,
  riseQuestion: "What if I get a pay rise?",
  riseValue: (amount) => `+${amount} a year`,
  riseValueText: (amount) => `${amount} more a year`,
  riseNoText: "No pay rise",
  riseLine: (show) => ["Take-home ", ` a ${show}, `, ""],
  riseKeep: (cents, help) =>
    `You keep ${cents}c of each extra dollar; ${help ? "the rest goes to tax, Medicare and HELP" : "the rest goes to tax and Medicare"}.`,
  riseEmpty: "Move the slider to see what a pay rise adds after tax.",
  riseSaid: (takeHome, rise, show) => `Take-home ${takeHome} a ${show}, +${rise}`,
  showPer: "Show per",
  figureCaption: (label, fromJob, show) => `${label}${fromJob ? " from this job" : ""} per ${show}`,
  rates: ["Average tax rate ", " · Marginal rate ", ""],
  ratesEmpty: (dash) => `Average tax rate ${dash} · Marginal rate ${dash}`,
  payBeforeTaxShort: "Pay before tax",
  superOnTop: "Super, paid on top",
  youKeep: (share) => `${share} you keep`,
  splitRight: (pct, helpOn) => `${pct} tax${helpOn ? ", Medicare and HELP" : " and Medicare"}`,
  whereGoes: (show) => `Where your pay goes, per ${show}`,
  rowTax: "Income tax (after offsets)",
  rowMedicare: "Medicare levy",
  rowHelp: "HELP repayment",
  rowHelpOff: "Off",
  rowTakeHome: "Take-home pay",
  rowSuper: (rate) => `Employer super (${rate}, paid on top)`,
  medicareSingle: (v) =>
    `Single person: no levy up to ${v.lower} of taxable income, then ${v.shade} of the amount over, and the full ${v.rate} above ${v.upper}.`,
  medicareOff:
    "Medicare levy not included, as you chose. Check you qualify for an exemption first.",
  medicareOffSource: "ATO Medicare levy exemption",
  deductionIncluded: (amount) =>
    `Includes the ${amount} standard deduction; your employer may withhold a little more each pay and the difference comes back at tax time.`,
  provisional: "Provisional",
  provisionalFallback: "Some rules are provisional.",
  copyLink: "Copy link to this result",
  localNote: "Runs in your browser. Nothing is sent to us.",
  copied: "Link copied",
  copyFailed: "Could not copy. Copy the address from your browser instead.",
  chartsLabel: "Your result in charts",
  bracket: {
    title: "Your tax bracket",
    marginal: "marginal rate",
    plusLevy: (rate) => `, plus ${rate} Medicare levy`,
    axis: "Taxable income, A$ thousands",
    invalid: "Enter a valid amount to see your tax bracket.",
    columns: ["Band (taxable income)", "Rate", "Your tax in this band"],
    tableCaption: (taxable, fy) =>
      `Income tax by band${taxable === undefined ? "" : ` on ${taxable} taxable income`}, ${fy}`,
  },
  cut: {
    title: (fy) => `Your ${fy} tax cut`,
    plain: "Your tax cut",
    line: (more, perShown, show) => `${more ? "more" : "less"} a year · ${perShown} a ${show}`,
    keyTax: "Income tax and Medicare levy",
    keySave: "Saved",
    comes: (amount) => `Where the ${amount} comes from`,
    rateChanges: "Tax rate changes",
    rateChange: (from, to) => `${from} to ${to}`,
    deduction: (max) => `New ${max} standard deduction`,
    showTable: "Show as a table",
    noPrevious: (fy) => `The calculator has no year before ${fy} to compare with.`,
    invalid: "Enter a valid amount to compare the two years.",
    columns: ["Income year", "Income tax", "Medicare levy", "Take-home"],
    tableCaption: (gross, previous, current) =>
      `Tax on ${gross ?? "this pay"}, ${previous} and ${current}`,
  },
  disclaimer: {
    lead: "Estimate only, not tax advice. Assumes this is your only income (unless you enter another job), you are single with no dependants, and you have no other deductions or offsets. Includes the Low Income Tax Offset and the Medicare levy low-income reduction. Does not include the Medicare levy surcharge, SAPTO, salary sacrifice or fringe benefits. For those, or for a family, use the ",
    estimator: "ATO income tax estimator",
    medicare: " The ATO Medicare levy calculator covers families, SAPTO and the surcharge: ",
    medicareLink: "ATO Medicare levy calculator",
  },
  how: {
    summary: "How we worked this out",
    sub: (fy) => `FY ${fy}, figures for the year`,
    enter: "Enter a valid amount to see the working.",
    payslip:
      "Your employer withholds tax each pay using ATO tax tables, so a payslip can differ by a few dollars. We work out tax for the whole year and divide it; any difference is settled when you lodge your return.",
  },
  working: {
    pay: (gross, rate, employerSuper) =>
      `Pay from this job ${gross} a year, super not included. Employer super ${rate} = ${employerSuper}, paid on top.`,
    deduction: (notApplied, amount, taxable) =>
      `Standard deduction ${notApplied ? "not applied" : amount}, so taxable income is ${taxable}.`,
    tax: (v) => `Income tax: ${v.base} + ${v.rate} × (${v.taxable} − ${v.from}) = ${v.tax}.`,
    lito: (amount) => `Low Income Tax Offset: ${amount}.`,
    medicareNone: "Medicare levy: none for this residency.",
    medicareOff: "Medicare levy: not included, as you chose.",
    medicare: (rate, amount) =>
      `Medicare levy: ${rate} of taxable income, reduced on low incomes = ${amount}.`,
    help: (amount) => `Study loan repayment on your taxable income: ${amount}.`,
    takeHome: (v) => [
      `Take-home: ${v.gross} − ${v.tax} + ${v.lito} − ${v.levy} − ${v.loan} = `,
      ` a year. Each period is the year divided by 52, 26 or 12 and rounded to the cent. Marginal rate ${v.marginal} is tax plus Medicare on your next dollar; it leaves out study loan repayments.`,
    ],
  },
  help: {
    title: "Your HELP repayment band",
    wholeIncome: "of your whole repayment income",
    overBand: "on each dollar over the band start",
    axis: "Taxable income used as repayment income, A$ thousands",
    turnOn: "Turn on the HELP box to see your repayment.",
    combined: (taxable, repayment) =>
      `Your combined taxable income of ${taxable} puts you in this band. The ${repayment} a year is only this job's share of that.`,
    under: (taxable) =>
      `On a taxable income of ${taxable} you are under the minimum repayment income, so no repayment is due.`,
    repay: (taxable, repayment) =>
      `On a taxable income of ${taxable} you repay ${repayment} a year.`,
    invalid: "Enter a valid amount to see your repayment band.",
    tableCaption: (fy) => `HELP repayment bands, ${fy}`,
    columns: ["Repayment income", "Compulsory repayment", "Your band"],
    here: "You are here",
  },
  medicareFigure: {
    title: "Your Medicare levy and the low-income reduction",
    yearSingle: "a year, single person",
    axis: "Taxable income, A$ thousands",
    invalid: "Enter a valid amount to see your levy.",
    noLevyShown:
      "For a foreign resident or working holiday maker this calculator charges no Medicare levy, so the line above shows what a resident would pay.",
    combined: (taxable) =>
      `Your combined taxable income is ${taxable}. The levy shown is only this job's share of that.`,
    atOrUnder: (lower) => `Your taxable income is at or under ${lower}, so no levy is due.`,
    reduction: (rate) =>
      `Your taxable income is in the reduction range, so you pay part of the full ${rate} levy.`,
    full: (upper, rate) =>
      `Your taxable income is above ${upper}, so you pay the full ${rate} levy.`,
    tableCaption: (fy) => `Medicare levy by taxable income, single person, ${fy}`,
    columns: ["Taxable income", "Medicare levy"],
    upTo: (lower) => `Up to ${lower}`,
    nil: "Nil",
    shade: (shade, lower) => `${shade}c for each A$1 over ${lower}`,
    over: (upper) => `Over ${upper}`,
    fullRate: (rate) => `${rate} of all your taxable income`,
  },
  showTable: "Show as a table",
}

const PAY_COPY: Record<Locale, PayCopy> = { en: PAY_COPY_EN, zh: PAY_COPY_ZH }
const useCopy = (): PayCopy => PAY_COPY[useLocale()]

/** Keyboard map, shown in the "?" popover (scoped to the calculator); the actions come from the copy, in this order. */
const SHORTCUT_KEYS: readonly { keys: string[]; sep?: string }[] = [
  { keys: ["↑", "↓"], sep: "/" },
  { keys: ["Shift", "↑ ↓"] },
  { keys: ["Alt", "↑ ↓"] },
  { keys: ["Enter"] },
  { keys: ["←", "→"], sep: "/" },
  { keys: ["?"] },
  { keys: ["Esc"] },
]

const selectClass =
  "border-input dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/50 h-11 w-full rounded-md border bg-transparent px-3 text-base outline-none focus-visible:ring-[3px]"
/** Secondary text fields: 44 px tall, 16 px text at every width (the Input primitive drops to 14 px from md). */
const fieldClass = "h-11 md:text-base"
const kbdClass =
  "border-border bg-card text-foreground inline-grid h-6 min-w-6 place-items-center rounded-[5px] border border-b-2 px-1.5 font-mono text-[13px]"
const roll =
  "inline-block animate-[pc-roll-in_220ms_cubic-bezier(.2,.8,.2,1)] motion-reduce:animate-none"

const percent = (bp: number) =>
  `${new Intl.NumberFormat("en-AU", { maximumFractionDigits: 1 }).format(bp / 100)}%`
/** A deduction: "−A$604.61", but a zero deduction shows "A$0.00" without a sign. */
const minus = (value: Money) => (isZero(value) ? formatAud(value) : `−${formatAud(value)}`)
const whole = (value: Money) => formatAud(value).replace(/\.00$/, "")
const isZero = (value: Money) => compare(value, money(0, "AUD")) === 0
const periodsPer: Record<ShownPeriod, number> = { year: 1, month: 12, fortnight: 26, week: 52 }

/** Numerals shrink for long values so the 390 px field never overflows (max "10,000,000.00"). */
const heroSize = (text: string) =>
  text.length > 11 ? "text-xl" : text.length > 8 ? "text-2xl" : "text-[1.75rem]"

const Kbd = ({ keys, sep = "+" }: { keys: string[]; sep?: string }) => (
  <span className="inline-flex items-center gap-1">
    {keys.map((k, n) => (
      <span key={k} className="inline-flex items-center gap-1">
        {n > 0 && <span className="text-muted-foreground">{sep}</span>}
        <kbd className={kbdClass}>{k}</kbd>
      </span>
    ))}
  </span>
)

/** "?" button and the non-modal shortcuts list; Esc or a click outside closes it and focus returns to the button. */
function Shortcuts({
  open,
  onOpenChange,
  buttonRef,
  stepLabel,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  buttonRef: React.RefObject<HTMLButtonElement | null>
  stepLabel: string
}) {
  const id = useId()
  const keys = useCopy().shortcuts
  const panel = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    panel.current?.focus()
    const away = (e: MouseEvent) => {
      const t = e.target as Node
      if (!panel.current?.contains(t) && !buttonRef.current?.contains(t)) onOpenChange(false)
    }
    document.addEventListener("mousedown", away)
    return () => document.removeEventListener("mousedown", away)
  }, [open, onOpenChange, buttonRef])
  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-label={keys.button}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-keyshortcuts="?"
        onClick={() => onOpenChange(!open)}
        className="border-border bg-card text-muted-foreground hover:text-foreground focus-visible:ring-ring aria-expanded:border-primary aria-expanded:text-primary grid size-11 place-items-center rounded-md border outline-none focus-visible:ring-[3px]"
      >
        <Keyboard className="size-5" aria-hidden="true" />
      </button>
      {open && (
        <div
          ref={panel}
          id={id}
          role="dialog"
          aria-label={keys.title}
          tabIndex={-1}
          className="border-border bg-card absolute top-full right-0 z-20 mt-1.5 grid w-80 max-w-[calc(100vw-2rem)] origin-top-right animate-[pc-pop-in_150ms_cubic-bezier(.2,.8,.2,1)] gap-2.5 rounded-lg border p-4 shadow-lg outline-none motion-reduce:animate-none"
        >
          <b>{keys.title}</b>
          <dl className="grid gap-2 text-sm">
            {SHORTCUT_KEYS.map((s, n) => (
              <div key={n} className="grid grid-cols-[7.5rem_1fr] items-center gap-2.5">
                <dt>
                  <Kbd keys={s.keys} sep={s.sep} />
                </dt>
                <dd>{n === 0 ? stepLabel : keys.actions[n]}</dd>
              </div>
            ))}
          </dl>
          <span className="text-muted-foreground text-sm">{keys.only}</span>
        </div>
      )}
    </div>
  )
}

/** Pay period chips: a radiogroup with roving tabindex; Left/Right (and Up/Down, Home/End) move and select. */
function PeriodChips({
  value,
  onChange,
}: {
  value: PayPeriod
  onChange: (value: PayPeriod) => void
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const copy = useCopy()
  const move = (e: KeyboardEvent<HTMLButtonElement>, k: number) => {
    const last = PERIOD_CHIPS.length - 1
    const to =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? k === last
          ? 0
          : k + 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? k === 0
            ? last
            : k - 1
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? last
              : undefined
    if (to === undefined) return
    e.preventDefault()
    onChange(PERIOD_CHIPS[to]!)
    refs.current[to]?.focus()
  }
  return (
    <div
      role="radiogroup"
      aria-label={copy.paidPer}
      className="border-border bg-muted grid grid-cols-3 gap-1 border-t p-1"
    >
      {PERIOD_CHIPS.map((c, k) => {
        const on = c === value
        return (
          <button
            key={c}
            ref={(el) => {
              refs.current[k] = el
            }}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            aria-keyshortcuts="ArrowLeft ArrowRight"
            onClick={() => onChange(c)}
            onKeyDown={(e) => move(e, k)}
            className={`focus-visible:ring-ring h-11 min-w-0 rounded-md px-2 text-[15px] outline-none focus-visible:ring-[3px] focus-visible:ring-inset ${on ? "bg-card text-primary ring-primary font-semibold ring-[1.5px] ring-inset" : "text-muted-foreground hover:text-foreground font-medium"}`}
          >
            {copy.periods[c]}
          </button>
        )
      })}
    </div>
  )
}

/** Breakdown row label: icon chip, text, share and the pattern key of its bar segment. */
const PartLabel = ({
  icon,
  tone,
  text,
  share,
  fill,
  base,
}: {
  icon: LucideIcon
  tone: ChipTone
  text: ReactNode
  share?: string
  fill?: FillKey
  base: string
}) => (
  <span className="flex items-center gap-2.5">
    <IconChip icon={icon} tone={tone} />
    <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
      <span>{text}</span>
      {share && <span className="text-muted-foreground text-[13px] font-normal">{share}</span>}
    </span>
    {fill && <FillKeySwatch base={base} fill={fill} />}
  </span>
)

interface SegmentedProps<T extends string> {
  legend: string
  name: string
  options: readonly { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
}

/** Native radios styled as a segmented control: arrow keys and labels come from the browser. */
function Segmented<T extends string>({
  legend,
  name,
  options,
  value,
  onChange,
}: SegmentedProps<T>) {
  return (
    <fieldset className="grid gap-1.5">
      <legend className="text-sm font-medium">{legend}</legend>
      <div className="bg-muted grid auto-cols-fr grid-flow-col gap-1 rounded-lg p-1">
        {options.map((o) => (
          <label key={o.value} className="relative">
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
              className="peer sr-only"
            />
            <span className="text-muted-foreground peer-checked:bg-card peer-checked:text-primary peer-checked:ring-primary peer-focus-visible:ring-ring flex min-h-11 cursor-pointer items-center justify-center rounded-md px-3 text-sm font-medium peer-checked:ring-[1.5px] peer-focus-visible:ring-[3px]">
              {o.label}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

function Row({ label, value, strong }: { label: ReactNode; value: ReactNode; strong?: boolean }) {
  return (
    <tr className="border-border border-b last:border-b-0">
      <th scope="row" className={`py-2 pr-3 text-left ${strong ? "font-bold" : "font-normal"}`}>
        {label}
      </th>
      <td className={`py-2 text-right tabular-nums ${strong ? "font-bold" : ""}`}>{value}</td>
    </tr>
  )
}

/** Each step of the engine with the ATO page it comes from; rates come from the rule table, never from this file. */
function Working({ form, result }: { form: PayForm; result: TakeHomeResult }) {
  const copy = useCopy()
  const t = copy.working
  const year = incomeTaxYear(form.fy)
  const { annual } = result
  const taxable = annual.taxableIncome.amount / 100
  const table = bracketTable(year, form.residency)
  // Same "over A$x" rule as the engine, so A$45,000 exactly stays in the 15% band.
  const bracket = table.brackets[bracketPosition(table.brackets, taxable).index]!
  const fy = displayYear(form.fy)
  const src = (kind: SourceKind, s: { label: string; url: string; checkedOn: string }) => (
    <SourceLine
      sources={[{ label: copy.sourceLabel(kind, s.label), href: s.url }]}
      financialYear={fy}
      checked={copy.checkedOn(s.checkedOn)}
    />
  )
  const levy =
    form.residency === "foreign" ||
    (form.residency === "working-holiday" && year.workingHoliday.medicare === "none")
  const hasLito =
    form.residency === "resident" ||
    (form.residency === "working-holiday" && year.workingHoliday.lito)
  const step = "grid gap-1"
  const takeHome = t.takeHome({
    gross: formatAud(annual.gross),
    tax: formatAud(annual.incomeTax),
    lito: formatAud(annual.lito),
    levy: formatAud(annual.medicareLevy),
    loan: formatAud(annual.studyLoanRepayment),
    marginal: percent(annual.marginalRateBp),
  })
  return (
    <ol className="grid list-decimal gap-4 pl-5 text-sm">
      <li className={step}>
        <span>
          {t.pay(
            formatAud(annual.gross),
            percent(year.superGuarantee.rateBp),
            formatAud(annual.employerSuper),
          )}
        </span>
        {src("super", year.superGuarantee.source)}
      </li>
      {year.standardDeduction && (
        <li className={step}>
          <span>
            {t.deduction(
              isZero(annual.standardDeduction),
              minus(annual.standardDeduction),
              formatAud(annual.taxableIncome),
            )}
          </span>
          {src("deduction", year.standardDeduction.source)}
        </li>
      )}
      <li className={step}>
        <span>
          {t.tax({
            base: `A$${bracket.baseDollars.toLocaleString("en-AU")}`,
            rate: percent(bracket.rateBp),
            taxable: taxable.toLocaleString("en-AU"),
            from: bracket.from.toLocaleString("en-AU"),
            tax: formatAud(annual.incomeTax),
          })}
        </span>
        {src(form.residency, table.source)}
      </li>
      {hasLito && (
        <li className={step}>
          <span>{t.lito(formatAud(annual.lito))}</span>
          {src("lito", year.lito.source)}
        </li>
      )}
      <li className={step}>
        <span>
          {levy
            ? t.medicareNone
            : !form.medicare
              ? t.medicareOff
              : t.medicare(percent(year.medicare.rateBp), formatAud(annual.medicareLevy))}
        </span>
        {src("medicare", year.medicare.source)}
      </li>
      {form.help && (
        <li className={step}>
          <span>{t.help(formatAud(annual.studyLoanRepayment))}</span>
          {src("help", year.studyLoan.source)}
        </li>
      )}
      <li>
        {takeHome[0]}
        <b>{formatAud(annual.takeHome)}</b>
        {takeHome[1]}
      </li>
    </ol>
  )
}

const PRESETS = [50_000, 70_000, 85_000, 100_000, 120_000, 150_000] as const

/** Quick yearly salaries: each sets the amount and Paid per = Year. Toggle buttons with aria-pressed. */
function SalaryPresets({
  amount,
  period,
  onPick,
}: {
  amount: string
  period: PayPeriod
  onPick: (amount: string) => void
}) {
  const copy = useCopy()
  const current = period === "year" ? amount.replaceAll(",", "").trim() : ""
  return (
    <div role="group" aria-label={copy.quickSalaries} className="flex flex-wrap gap-1.5">
      {PRESETS.map((p) => {
        const on = current === String(p)
        return (
          <button
            key={p}
            type="button"
            aria-pressed={on}
            onClick={() => onPick(String(p))}
            className={`focus-visible:ring-ring h-11 rounded-full border px-3 text-sm font-medium tabular-nums outline-none focus-visible:ring-[3px] md:h-9 ${on ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground hover:text-foreground"}`}
          >
            {copy.presetChip(p / 1000)}
          </button>
        )
      })}
    </div>
  )
}

const RISE_MAX = 30_000
const RISE_STEP = 500

/** Free take-home pay estimate: the engine runs in the browser, inputs live in the URL, nothing is sent anywhere. */
export function PayCalculator({ variant = "pay" }: { variant?: PayVariant }) {
  const id = useId()
  const locale = useLocale()
  const copy = PAY_COPY[locale]
  const defaults = VARIANT_DEFAULTS[variant]
  const emphasis = EMPHASIS[variant]
  const emphasisText = copy.emphasis[variant]
  const [form, setForm] = useState<PayForm>(defaults)
  const [applied, setApplied] = useState<PayForm>(defaults)
  const [moreOpen, setMoreOpen] = useState(false)
  const [copied, setCopied] = useState<"idle" | "ok" | "failed">("idle")
  const [keysOpen, setKeysOpen] = useState(false)
  const [rise, setRise] = useState(0)
  const [riseSaid, setRiseSaid] = useState("")
  // Motion only after the first user change, so nothing animates on page load.
  const [moved, setMoved] = useState(false)
  const keysButton = useRef<HTMLButtonElement>(null)
  const returnFocus = useRef<HTMLElement | null>(null)
  const amountRef = useRef<HTMLInputElement>(null)
  /** Digits and points before the caret at the last keystroke; restored after the separators are redrawn. */
  const caretDigits = useRef<number | null>(null)
  const amountText = groupAmount(form.amount)

  useLayoutEffect(() => {
    const el = amountRef.current
    if (caretDigits.current === null || !el || document.activeElement !== el) return
    const at = caretAfter(el.value, caretDigits.current)
    el.setSelectionRange(at, at)
    caretDigits.current = null
  })

  const onAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const caret = e.target.selectionStart ?? raw.length
    caretDigits.current = (raw.slice(0, caret).match(/[\d.]/g) ?? []).length
    set({ amount: normalizeAmountInput(raw) })
  }

  // Restore a shared link after hydration so the prerendered default and the first client render match.
  useEffect(() => {
    if (!window.location.search) return
    const parsed = parseForm(new URLSearchParams(window.location.search), defaults)
    setForm(parsed)
    setApplied(parsed)
    setMoreOpen(
      (variant !== "help" && parsed.help !== defaults.help) ||
        parsed.residency !== defaults.residency ||
        parsed.other !== defaults.other ||
        parsed.medicare !== defaults.medicare ||
        parsed.standardDeduction !== defaults.standardDeduction,
    )
    // defaults and variant are constants of this page
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const commit = (next: PayForm) => {
    setApplied(next)
    setMoved(true)
    const query = formToParams(next, defaults).toString()
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
    )
  }

  // 150 ms debounce, then the URL follows the inputs without adding history entries.
  useEffect(() => {
    if (form === applied) return
    const timer = setTimeout(() => commit(form), 150)
    return () => clearTimeout(timer)
    // commit only reads its argument and stable setters
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [form, applied])

  const set = (patch: Partial<PayForm>) => setForm((f) => ({ ...f, ...patch }))
  const outcome = evaluate(applied, locale)
  const live = evaluate(form)
  const result = outcome.kind === "ok" ? outcome.result : undefined
  const shown = result?.perPeriod[applied.show]
  const invalid = (field: string) => live.kind === "error" && live.field === field
  const rules = incomeTaxYear(applied.fy)
  const table = bracketTable(rules, applied.residency)
  const taxable = result ? result.annual.taxableIncome.amount / 100 : undefined
  const position = taxable === undefined ? undefined : bracketPosition(table.brackets, taxable)
  const cut = result ? taxCut(applied) : undefined
  const riseCap = result ? riseCapDollars(result) : 0
  const riseMax = Math.min(RISE_MAX, Math.floor(riseCap / RISE_STEP) * RISE_STEP)
  const riseUsed = Math.min(rise, riseMax)
  const raise = result ? payRise(applied, result, riseUsed) : undefined
  const shares = shown ? payShares(shown) : undefined
  const share = (key: string) => {
    const s = shares?.find((x) => x.key === key)
    return s ? percent(s.shareBp) : undefined
  }

  // The visible slider output updates at once; screen readers hear it after 500 ms of no change.
  const riseText =
    raise && riseUsed > 0
      ? copy.riseSaid(formatAud(raise.takeHomePerShown), formatAud(raise.perShown), applied.show)
      : ""
  useEffect(() => {
    const timer = setTimeout(() => setRiseSaid(riseText), 500)
    return () => clearTimeout(timer)
  }, [riseText])

  const copyLink = async () => {
    const query = formToParams(applied, defaults).toString()
    const url = `${window.location.origin}${window.location.pathname}${query ? `?${query}` : ""}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied("ok")
    } catch {
      setCopied("failed")
    }
  }

  const stepCents = amountStepCents(form.period)
  const stepLabel = copy.shortcuts.changeBy(whole(money(stepCents, "AUD")))
  const onAmountKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      commit(form)
      return
    }
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return
    e.preventDefault()
    const next = stepAmount(form.amount, form.period, e.key === "ArrowUp" ? 1 : -1, {
      shift: e.shiftKey,
      alt: e.altKey,
    })
    if (next === undefined) return
    const nextForm = { ...form, amount: next }
    setForm(nextForm)
    commit(nextForm)
  }
  // "?" is not a valid amount character, so it is safe to take it inside the fields too.
  const onRootKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "?" && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
      if (!keysOpen && document.activeElement instanceof HTMLElement)
        returnFocus.current = document.activeElement
      setKeysOpen(true)
    } else if (e.key === "Escape" && keysOpen) {
      e.preventDefault()
      setKeysOpen(false)
      // Back to where "?" was pressed, or to the button that opened the list.
      ;(returnFocus.current ?? keysButton.current)?.focus()
      returnFocus.current = null
    }
  }

  const standardDeduction = incomeTaxYear(form.fy).standardDeduction
  const helpToggle = (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        className="accent-primary mt-1 size-5"
        checked={form.help}
        onChange={(e) => set({ help: e.target.checked })}
      />
      <span>
        <b className="block text-sm">{copy.helpTitle}</b>
        <span className="text-muted-foreground text-sm">{copy.helpHint}</span>
      </span>
    </label>
  )
  const figure = shown?.[emphasis.key]
  // The engine attributes to this job only its share of Medicare and HELP when there is a second income.
  const otherJob = (parseAmount(applied.other)?.amount ?? 0) > 0
  const levyApplies = !(
    applied.residency === "foreign" ||
    (applied.residency === "working-holiday" && rules.workingHoliday.medicare === "none")
  )
  const isHour = form.period === "hour"
  const isDay = form.period === "day"
  const riseLine = copy.riseLine(applied.show)
  const resultId = "pay-result"
  const errorId = outcome.kind === "error" ? `${id}-error` : undefined
  const fy = displayYear(applied.fy)
  const dash = "–"
  const dollarsText = (n: number) => whole(money(n * 100, "AUD"))

  return (
    <div className="grid gap-4" onKeyDown={onRootKey} data-testid="pay-calculator">
      <ChartPatterns base={id} />
      <div className={toolPanels}>
        <div className={toolInputPanel}>
          <div className="flex min-h-11 items-center justify-between gap-3">
            <h3 className={toolPanelHeading}>{copy.yourPay}</h3>
            <Shortcuts
              open={keysOpen}
              onOpenChange={setKeysOpen}
              buttonRef={keysButton}
              stepLabel={stepLabel}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-amount`} className="text-base font-semibold">
              {copy.payBeforeTax}
            </Label>
            <div className={toolHeroBox}>
              <div className="flex h-14 items-center gap-2 px-3.5">
                <span className={toolHeroPrefix} aria-hidden="true">
                  A$
                </span>
                <input
                  id={`${id}-amount`}
                  inputMode="decimal"
                  autoComplete="off"
                  ref={amountRef}
                  className={`${toolHeroInput} ${heroSize(amountText)}`}
                  value={amountText}
                  onChange={onAmountChange}
                  onKeyDown={onAmountKey}
                  aria-invalid={invalid("amount")}
                  aria-describedby={`${id}-amount-hint ${errorId ?? ""}`.trim()}
                  aria-keyshortcuts="ArrowUp ArrowDown Shift+ArrowUp Shift+ArrowDown Alt+ArrowUp Alt+ArrowDown Enter"
                />
              </div>
              <PeriodChips value={form.period} onChange={(period) => set({ period })} />
              {(isHour || isDay) && (
                <div className="border-border grid grid-cols-[1fr_9rem] items-center gap-3 border-t py-2 pr-2 pl-3.5">
                  <Label htmlFor={isHour ? `${id}-hpw` : `${id}-dpw`}>
                    {isHour ? copy.hoursLabel : copy.daysLabel}
                  </Label>
                  {isHour ? (
                    <Input
                      id={`${id}-hpw`}
                      inputMode="decimal"
                      autoComplete="off"
                      className={fieldClass}
                      value={form.hoursPerWeek}
                      onChange={(e) => set({ hoursPerWeek: e.target.value })}
                      aria-invalid={invalid("hours")}
                      aria-describedby={errorId}
                    />
                  ) : (
                    <Input
                      id={`${id}-dpw`}
                      inputMode="numeric"
                      autoComplete="off"
                      className={fieldClass}
                      value={form.daysPerWeek}
                      onChange={(e) => set({ daysPerWeek: e.target.value })}
                      aria-invalid={invalid("days")}
                      aria-describedby={errorId}
                    />
                  )}
                </div>
              )}
            </div>
            <p
              id={`${id}-amount-hint`}
              className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
            >
              <span>{result ? copy.yearlyGross(whole(result.annual.gross)) : dash}</span>
              <span className="hidden items-center gap-1.5 md:inline-flex">
                <Kbd keys={["↑", "↓"]} sep="/" />{" "}
                {copy.hintChangeBy(whole(money(stepCents, "AUD")))}
              </span>
            </p>
            <SalaryPresets
              amount={form.amount}
              period={form.period}
              onPick={(amount) => {
                const next = { ...form, amount, period: "year" as const }
                setForm(next)
                commit(next)
              }}
            />
          </div>
          <div className="border-border grid gap-4 border-t pt-4">
            <Segmented
              legend={copy.superLegend}
              name={`${id}-super`}
              options={[
                { value: "excl", label: copy.superExcl },
                { value: "incl", label: copy.superIncl },
              ]}
              value={form.superIncluded ? "incl" : "excl"}
              onChange={(v) => set({ superIncluded: v === "incl" })}
            />
            <div className="grid gap-1.5">
              <Label htmlFor={`${id}-fy`}>{copy.incomeYear}</Label>
              <select
                id={`${id}-fy`}
                className={selectClass}
                value={form.fy}
                onChange={(e) => set({ fy: e.target.value })}
              >
                {INCOME_TAX_YEARS.toReversed().map((y) => (
                  <option key={y.financialYear} value={y.financialYear}>
                    {copy.yearOption(y.financialYear)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {variant === "help" && <div className="border-border border-t pt-4">{helpToggle}</div>}
          <details
            className="border-border group border-t pt-2"
            open={moreOpen}
            onToggle={(e) => setMoreOpen(e.currentTarget.open)}
          >
            <summary className="focus-visible:ring-ring flex min-h-11 cursor-pointer list-none flex-wrap items-center gap-x-2 rounded-sm text-sm font-medium outline-none focus-visible:ring-2 [&::-webkit-details-marker]:hidden">
              <ChevronDown
                className="size-4 transition-transform group-open:rotate-180 motion-reduce:transition-none"
                aria-hidden="true"
              />
              <span className="whitespace-nowrap">{copy.moreOptions}</span>
              <span className="text-muted-foreground hidden text-sm font-normal sm:inline">
                {variant === "help" ? copy.moreHint.help : copy.moreHint.other}
              </span>
            </summary>
            <div className="grid gap-4 pt-3">
              {variant !== "help" && helpToggle}
              <div className="grid gap-1.5">
                <Label htmlFor={`${id}-res`}>{copy.residencyLabel}</Label>
                <select
                  id={`${id}-res`}
                  className={selectClass}
                  value={form.residency}
                  onChange={(e) => set({ residency: e.target.value as PayForm["residency"] })}
                >
                  <option value="resident">{copy.residency.resident}</option>
                  <option value="foreign">{copy.residency.foreign}</option>
                  <option value="working-holiday">{copy.residency["working-holiday"]}</option>
                </select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={`${id}-other`}>{copy.otherLabel}</Label>
                <Input
                  id={`${id}-other`}
                  inputMode="decimal"
                  autoComplete="off"
                  className={fieldClass}
                  value={form.other}
                  onChange={(e) => set({ other: e.target.value })}
                  aria-invalid={invalid("other")}
                  aria-describedby={`${id}-other-hint ${errorId ?? ""}`.trim()}
                />
                <p id={`${id}-other-hint`} className="text-muted-foreground text-sm">
                  {copy.otherHint}
                </p>
              </div>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="accent-primary mt-1 size-5"
                  // Only residents pay the levy here: working holiday makers are "none" in the rules
                  // (income-tax.ts whmTreatment); revisit this coupling if that changes.
                  checked={form.medicare && form.residency === "resident"}
                  disabled={form.residency !== "resident"}
                  onChange={(e) => set({ medicare: e.target.checked })}
                />
                <span>
                  <b className="block text-sm">
                    {copy.medicareTitle(percent(incomeTaxYear(form.fy).medicare.rateBp))}
                  </b>
                  <span className="text-muted-foreground text-sm">{copy.medicareHint}</span>
                </span>
              </label>
              {standardDeduction && (
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="accent-primary mt-1 size-5"
                    checked={form.standardDeduction}
                    disabled={form.residency !== "resident"}
                    onChange={(e) => set({ standardDeduction: e.target.checked })}
                  />
                  <span>
                    <b className="block text-sm">
                      {copy.deductionTitle(
                        `A$${standardDeduction.maxDollars.toLocaleString("en-AU")}`,
                      )}
                    </b>
                    <span className="text-muted-foreground text-sm">
                      {copy.deductionHint(displayYear(form.fy))}
                    </span>
                  </span>
                </label>
              )}
            </div>
          </details>
          <div className="border-border grid gap-2 border-t pt-4">
            <div className="flex items-center gap-2.5">
              <IconChip icon={TrendingUp} tone="success" />
              <label htmlFor={`${id}-rise`} className="flex-1 font-semibold">
                {copy.riseQuestion}
              </label>
              <output htmlFor={`${id}-rise`} className="font-semibold tabular-nums">
                {riseUsed > 0 ? copy.riseValue(whole(money(riseUsed * 100, "AUD"))) : "A$0"}
              </output>
            </div>
            <input
              id={`${id}-rise`}
              type="range"
              min={0}
              max={riseMax}
              step={RISE_STEP}
              value={riseUsed}
              disabled={!result || riseMax === 0}
              onChange={(e) => setRise(Number(e.target.value))}
              aria-valuetext={
                riseUsed > 0
                  ? copy.riseValueText(whole(money(riseUsed * 100, "AUD")))
                  : copy.riseNoText
              }
              className="accent-success h-11 w-full cursor-pointer"
            />
            <div
              aria-hidden="true"
              className="text-muted-foreground -mt-2 flex justify-between text-[13px]"
            >
              <span>A$0</span>
              <span>A$10k</span>
              <span>A$20k</span>
              <span>A$30k</span>
            </div>
            <p className="grid min-h-11 gap-0.5 text-[15px]">
              {raise && riseUsed > 0 ? (
                <>
                  <span>
                    {riseLine[0]}
                    <b className="tabular-nums">{formatAud(raise.takeHomePerShown)}</b>
                    {riseLine[1]}
                    <b className="text-success tabular-nums">+{formatAud(raise.perShown)}</b>
                    {riseLine[2]}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    {copy.riseKeep(raise.keptCents, applied.help)}
                  </span>
                </>
              ) : (
                <span>{copy.riseEmpty}</span>
              )}
            </p>
            <span className="sr-only" aria-live="polite">
              {riseSaid}
            </span>
          </div>

          <div
            className={`${toolPanelFoot} border-border text-muted-foreground hidden flex-wrap items-center gap-x-4 gap-y-2 border-t pt-4 text-sm md:flex`}
            data-testid="pay-shortcuts-hint"
          >
            <span className="inline-flex items-center gap-1.5">
              <Kbd keys={["↑", "↓"]} sep="/" /> {copy.shortcuts.hintAmount}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Kbd keys={["←", "→"]} sep="/" /> {copy.shortcuts.hintPeriod}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Kbd keys={["?"]} /> {copy.shortcuts.hintAll}
            </span>
          </div>
        </div>

        <a href={`#${resultId}`} className={toolPeek}>
          <emphasis.icon className="size-[18px]" aria-hidden="true" />
          <span className="text-sm font-medium">{copy.per(emphasisText.short, applied.show)}</span>
          <b className="ml-auto text-lg tabular-nums">{figure ? formatAud(figure) : dash}</b>
          <ChevronDown className="size-4" aria-hidden="true" />
        </a>
        <div id={resultId} data-testid="pay-result" className={toolResultPanel}>
          <h3 className={toolPanelHeading}>{emphasisText.title}</h3>
          <Segmented
            legend={copy.showPer}
            name={`${id}-show`}
            options={SHOWN_PERIODS.map((p) => ({ value: p, label: copy.shown[p] }))}
            value={form.show}
            onChange={(v) => set({ show: v })}
          />
          {/* The figure, the bar and the table keep their size in the error state, so nothing moves while typing. */}
          {/* Only the figure and the rate line are announced; the rest of the panel is read on demand. */}
          <div
            className="grid gap-1"
            aria-live="polite"
            aria-atomic="true"
            data-testid="pay-figure"
          >
            <span className="text-muted-foreground text-sm">
              {copy.figureCaption(emphasisText.label, otherJob && variant !== "pay", applied.show)}
            </span>
            <div className={`${toolResultSlot} overflow-hidden`}>
              {figure ? (
                <span className={toolResultFigure}>
                  <span key={figure.amount} className={moved ? roll : "inline-block"}>
                    {formatAud(figure)}
                  </span>
                </span>
              ) : (
                <p id={errorId} className="font-medium">
                  {outcome.kind === "error" && outcome.message}
                </p>
              )}
            </div>
            <span className="text-muted-foreground text-sm">
              {result ? (
                <>
                  {copy.rates[0]}
                  <b className="text-foreground font-semibold">
                    {percent(result.annual.averageRateBp)}
                  </b>
                  {copy.rates[1]}
                  <b className="text-foreground font-semibold">
                    {percent(result.annual.marginalRateBp)}
                  </b>
                  {copy.rates[2]}
                </>
              ) : (
                copy.ratesEmpty(dash)
              )}
            </span>
          </div>
          <div className="grid gap-1.5">
            <div className="text-muted-foreground flex justify-between gap-3 text-[13px]">
              <span>
                {copy.payBeforeTaxShort}{" "}
                <b className="text-foreground font-semibold tabular-nums">
                  {shown ? formatAud(shown.gross) : dash}
                </b>
              </span>
              <span>{copy.superOnTop}</span>
            </div>
            <PaySplitBar base={id} period={shown} animate={moved} />
            <div className="flex justify-between gap-3 text-[13px] font-medium">
              <span>{shares ? copy.youKeep(share("takeHome") ?? "") : dash}</span>
              <span>
                {shares && shown
                  ? copy.splitRight(
                      percent(10_000 - (shares[0]?.shareBp ?? 0)),
                      !isZero(shown.studyLoanRepayment),
                    )
                  : ""}
              </span>
            </div>
          </div>
          <div className="bg-card rounded-md px-3">
            <table className="w-full text-[15px]">
              <caption className="sr-only">{copy.whereGoes(applied.show)}</caption>
              <tbody>
                <Row label={copy.payBeforeTaxShort} value={shown ? formatAud(shown.gross) : dash} />
                <Row
                  label={
                    <PartLabel
                      base={id}
                      icon={Landmark}
                      tone="primary"
                      text={copy.rowTax}
                      share={share("tax")}
                      fill="tax"
                    />
                  }
                  value={shown ? minus(shown.incomeTaxAfterLito) : dash}
                />
                <Row
                  strong={variant === "medicare"}
                  label={
                    <PartLabel
                      base={id}
                      icon={HeartPulse}
                      tone="warning"
                      text={copy.rowMedicare}
                      share={share("medicare")}
                      fill="medicare"
                    />
                  }
                  value={shown ? minus(shown.medicareLevy) : dash}
                />
                <Row
                  strong={variant === "help"}
                  label={
                    <PartLabel
                      base={id}
                      icon={GraduationCap}
                      tone="violet"
                      text={copy.rowHelp}
                      share={applied.help ? share("help") : copy.rowHelpOff}
                      fill="help"
                    />
                  }
                  value={shown ? minus(shown.studyLoanRepayment) : dash}
                />
                <Row
                  strong={variant === "pay"}
                  label={
                    <PartLabel
                      base={id}
                      icon={Wallet}
                      tone="success"
                      text={copy.rowTakeHome}
                      share={share("takeHome")}
                      fill="takeHome"
                    />
                  }
                  value={shown ? formatAud(shown.takeHome) : dash}
                />
                <Row
                  label={
                    <PartLabel
                      base={id}
                      icon={PiggyBank}
                      tone="muted"
                      text={copy.rowSuper(percent(rules.superGuarantee.rateBp))}
                      fill="super"
                    />
                  }
                  value={shown ? formatAud(shown.employerSuper) : dash}
                />
              </tbody>
            </table>
          </div>

          {variant === "medicare" && (
            <p className="text-muted-foreground text-sm">
              {copy.medicareSingle({
                lower: dollarsText(rules.medicare.singleLower),
                shade: percent(rules.medicare.shadeInBp),
                upper: dollarsText(rules.medicare.singleUpper),
                rate: percent(rules.medicare.rateBp),
              })}
            </p>
          )}
          {!applied.medicare && applied.residency === "resident" && (
            <>
              <p className="text-muted-foreground text-sm">{copy.medicareOff}</p>
              <SourceLine
                sources={[{ label: copy.medicareOffSource, href: MEDICARE_EXEMPTION_URL }]}
                financialYear={displayYear(applied.fy)}
                checked={copy.checkedOn(incomeTaxYear(applied.fy).medicare.source.checkedOn)}
              />
            </>
          )}
          {result && !isZero(result.annual.standardDeduction) && (
            <p className="text-muted-foreground text-sm">
              {copy.deductionIncluded(whole(result.annual.standardDeduction))}
            </p>
          )}
          {result?.annual.provisional.map((name) => (
            <p key={name} className="flex items-start gap-2 text-sm">
              <Badge variant="warning" className="shrink-0">
                {copy.provisional}
              </Badge>
              <span>
                {PROVISIONAL_TEXT[name]?.(applied.fy, locale) ?? copy.provisionalFallback}
              </span>
            </p>
          ))}
          <div className={`${toolPanelFoot} flex flex-wrap items-center justify-between gap-2`}>
            <Button type="button" variant="outline" size="sm" className="h-11" onClick={copyLink}>
              <Copy aria-hidden="true" />
              {copy.copyLink}
            </Button>
            <span className="text-muted-foreground text-sm">{copy.localNote}</span>
          </div>
          <p className="min-h-5 text-sm">
            {copied === "ok" && copy.copied}
            {copied === "failed" && copy.copyFailed}
          </p>
        </div>
      </div>

      <section aria-label={copy.chartsLabel} className="grid gap-4 md:grid-cols-2">
        {variant === "help" && (
          <HelpBandsFigure
            bands={rules.studyLoan.bands}
            result={result}
            taxable={taxable}
            on={applied.help}
            animate={moved}
            fy={fy}
            otherJob={otherJob}
          />
        )}
        {variant === "medicare" && (
          <MedicareFigure
            medicare={rules.medicare}
            result={result}
            taxable={taxable}
            animate={moved}
            fy={fy}
            levyApplies={levyApplies}
            otherJob={otherJob}
          />
        )}
        {variant === "pay" && (
          <>
            <figure className="border-border bg-card m-0 grid content-start gap-3 rounded-xl border p-4 md:p-5">
              <figcaption className="flex items-center gap-2.5 font-semibold">
                <IconChip icon={Landmark} tone="primary" />
                {copy.bracket.title}
              </figcaption>
              <p className="flex flex-wrap items-baseline gap-x-2">
                <b className="text-3xl font-bold tabular-nums">
                  {position ? percent(position.rateBp) : dash}
                </b>
                <span className="text-muted-foreground">
                  {copy.bracket.marginal}
                  {result && applied.residency === "resident"
                    ? copy.bracket.plusLevy(percent(rules.medicare.rateBp))
                    : ""}
                </span>
              </p>
              <BracketLadder
                brackets={table.brackets}
                taxable={taxable}
                position={position}
                animate={moved}
              />
              <p className="text-muted-foreground -mt-2 text-[13px]">{copy.bracket.axis}</p>
              <p className="text-sm">
                {position && taxable !== undefined
                  ? bracketCaption(table.brackets, taxable, locale)
                  : copy.bracket.invalid}
              </p>
              <BracketTable brackets={table.brackets} taxable={taxable} fy={fy} />
            </figure>

            <figure className="border-border bg-card m-0 grid content-start gap-3 rounded-xl border p-4 md:p-5">
              <figcaption className="flex items-center gap-2.5 font-semibold">
                <IconChip icon={Sparkles} tone="success" />
                {cut ? copy.cut.title(fy) : copy.cut.plain}
              </figcaption>
              {cut ? (
                <>
                  <p className="flex flex-wrap items-baseline gap-x-2">
                    <b
                      className={`text-3xl font-bold tabular-nums ${cut.saving.amount > 0 ? "text-success" : ""}`}
                    >
                      {whole(
                        cut.saving.amount >= 0 ? cut.saving : money(-cut.saving.amount, "AUD"),
                      )}
                    </b>
                    <span className="text-muted-foreground">
                      {copy.cut.line(
                        cut.saving.amount >= 0,
                        formatAud(
                          multiplyByRatio(
                            money(Math.abs(cut.saving.amount), "AUD"),
                            1,
                            periodsPer[applied.show],
                          ),
                        ),
                        applied.show,
                      )}
                    </span>
                  </p>
                  <TaxCutBars
                    base={id}
                    cut={cut}
                    previousLabel={displayYear(cut.previousFy)}
                    currentLabel={fy}
                    format={whole}
                    animate={moved}
                  />
                  <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
                    <span className="flex items-center gap-1.5">
                      <FillKeySwatch base={id} fill="tax" />
                      {copy.cut.keyTax}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <FillKeySwatch base={id} fill="save" />
                      {copy.cut.keySave}
                    </span>
                  </div>
                  <ul aria-label={copy.cut.comes(whole(cut.saving))} className="grid">
                    <li className="border-border flex min-h-11 items-center gap-2.5 border-t text-[15px]">
                      <IconChip icon={Percent} tone="primary" />
                      <span className="flex-1">
                        {copy.cut.rateChanges}
                        {cut.rateChanges.length > 0 &&
                          !isZero(cut.rateEffect) &&
                          ` (${cut.rateChanges.map((r) => copy.cut.rateChange(percent(r.fromBp), percent(r.toBp))).join(", ")})`}
                      </span>
                      <b className="tabular-nums">{whole(cut.rateEffect)}</b>
                    </li>
                    {!isZero(cut.deductionEffect) && (
                      <li className="border-border flex min-h-11 items-center gap-2.5 border-t text-[15px]">
                        <IconChip icon={CircleDollarSign} tone="success" />
                        <span className="flex-1">
                          {copy.cut.deduction(
                            `A$${(rules.standardDeduction?.maxDollars ?? 0).toLocaleString("en-AU")}`,
                          )}
                        </span>
                        <b className="tabular-nums">{whole(cut.deductionEffect)}</b>
                      </li>
                    )}
                  </ul>
                  <details className="group">
                    <summary className="text-primary focus-visible:ring-ring flex min-h-11 cursor-pointer list-none items-center gap-1.5 rounded-sm text-sm font-medium outline-none focus-visible:ring-2 [&::-webkit-details-marker]:hidden">
                      <ChevronDown
                        className="size-4 group-open:rotate-180 motion-reduce:transition-none"
                        aria-hidden="true"
                      />
                      {copy.showTable}
                    </summary>
                    <TaxCutTable form={applied} cutFy={cut.previousFy} />
                  </details>
                </>
              ) : (
                <p className="text-sm">{result ? copy.cut.noPrevious(fy) : copy.cut.invalid}</p>
              )}
            </figure>
          </>
        )}
      </section>

      <p className="text-muted-foreground flex max-w-[70ch] items-start gap-2 text-sm">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>
          {copy.disclaimer.lead}
          <a
            href={ATO_ESTIMATOR_URL}
            className="hover:text-foreground underline underline-offset-2"
          >
            {copy.disclaimer.estimator}
          </a>
          {locale === "zh" ? "。" : "."}
          {variant === "medicare" && (
            <>
              {copy.disclaimer.medicare}
              <a
                href={ATO_MEDICARE_CALCULATOR_URL}
                className="hover:text-foreground underline underline-offset-2"
              >
                {copy.disclaimer.medicareLink}
              </a>
              {locale === "zh" ? "。" : "."}
            </>
          )}
        </span>
      </p>

      <details className="bg-card border-border rounded-xl border p-4">
        <summary className="focus-visible:ring-ring flex min-h-11 cursor-pointer flex-wrap items-center gap-2 rounded-sm outline-none focus-visible:ring-2">
          <b>{copy.how.summary}</b>
          <span className="text-muted-foreground text-sm">{copy.how.sub(fy)}</span>
        </summary>
        <div className="grid gap-3 pt-3">
          {result ? (
            <Working form={applied} result={result} />
          ) : (
            <p className="text-sm">{copy.how.enter}</p>
          )}
          <p className="text-muted-foreground text-sm">{copy.how.payslip}</p>
        </div>
      </details>
    </div>
  )
}

const ShowTable = ({ children }: { children: ReactNode }) => {
  const copy = useCopy()
  return (
    <details className="group">
      <summary className="text-primary focus-visible:ring-ring flex min-h-11 cursor-pointer list-none items-center gap-1.5 rounded-sm text-sm font-medium outline-none focus-visible:ring-2 [&::-webkit-details-marker]:hidden">
        <ChevronDown className="size-4 group-open:rotate-180" aria-hidden="true" />
        {copy.showTable}
      </summary>
      {children}
    </details>
  )
}

/** Text equivalent of the ladder: each band, its rate and the tax this income pays in it. */
function BracketTable({
  brackets,
  taxable,
  fy,
}: {
  brackets: readonly Bracket[]
  taxable: number | undefined
  fy: string
}) {
  const locale = useLocale()
  const copy = PAY_COPY[locale]
  const rows = brackets.map((b, k) => {
    const next = brackets[k + 1]
    const range = bandRange(brackets, k, locale)
    const inBand =
      taxable === undefined ? 0 : Math.max(0, Math.min(taxable, next?.from ?? taxable) - b.from)
    const tax = multiplyByRatio(money(inBand * 100, "AUD"), b.rateBp, 10_000)
    return { header: range, cells: [percent(b.rateBp), taxable === undefined ? "–" : whole(tax)] }
  })
  return (
    <ShowTable>
      <SimpleTable
        caption={copy.bracket.tableCaption(
          taxable === undefined ? undefined : `A$${taxable.toLocaleString("en-AU")}`,
          fy,
        )}
        columns={copy.bracket.columns}
        rows={rows}
      />
    </ShowTable>
  )
}

/** Text equivalent of the tax-cut bars: the same inputs run through both years' rules. */
function TaxCutTable({ form, cutFy }: { form: PayForm; cutFy: string }) {
  const copy = useCopy()
  const rows = [cutFy, form.fy].flatMap((fy) => {
    const o = evaluate({ ...form, fy })
    if (o.kind !== "ok") return []
    const a = o.result.annual
    return [
      {
        header: displayYear(fy),
        cells: [
          whole(money(a.incomeTax.amount - a.lito.amount, "AUD")),
          whole(a.medicareLevy),
          whole(a.takeHome),
        ],
      },
    ]
  })
  const gross = evaluate(form)
  return (
    <SimpleTable
      caption={copy.cut.tableCaption(
        gross.kind === "ok" ? whole(gross.result.annual.gross) : undefined,
        displayYear(cutFy),
        displayYear(form.fy),
      )}
      columns={copy.cut.columns}
      rows={rows}
    />
  )
}

/** HELP page visual: the repayment bands as the ladder (step height = rate), you marked at your taxable income. */
function HelpBandsFigure({
  bands,
  result,
  taxable,
  on,
  animate,
  fy,
  otherJob,
}: {
  bands: readonly StudyLoanBand[]
  result: TakeHomeResult | undefined
  taxable: number | undefined
  on: boolean
  animate: boolean
  fy: string
  otherJob: boolean
}) {
  const locale = useLocale()
  const copy = PAY_COPY[locale]
  const t = copy.help
  const dash = "–"
  const position = taxable === undefined ? undefined : bracketPosition(bands, taxable)
  const band = position === undefined ? undefined : bands[position.index]
  const repayment = result?.annual.studyLoanRepayment
  const rows = bands.map((b, k) => ({
    header: bandRange(bands, k, locale),
    cells: [studyLoanRule(b, locale), position?.index === k ? t.here : ""],
  }))
  return (
    <figure className="border-border bg-card m-0 grid content-start gap-3 rounded-xl border p-4 md:col-span-2 md:p-5">
      <figcaption className="flex items-center gap-2.5 font-semibold">
        <IconChip icon={GraduationCap} tone="violet" />
        {t.title}
      </figcaption>
      <p className="flex flex-wrap items-baseline gap-x-2">
        <b className="text-3xl font-bold tabular-nums">{band ? percent(band.rateBp) : dash}</b>
        <span className="text-muted-foreground">
          {band?.ofWholeIncome ? t.wholeIncome : t.overBand}
        </span>
      </p>
      <BracketLadder brackets={bands} taxable={taxable} position={position} animate={animate} />
      <p className="text-muted-foreground -mt-2 text-[13px]">{t.axis}</p>
      <p className="text-sm">
        {band && taxable !== undefined && repayment
          ? !on
            ? t.turnOn
            : otherJob
              ? t.combined(`A$${taxable.toLocaleString("en-AU")}`, formatAud(repayment))
              : isZero(repayment)
                ? t.under(`A$${taxable.toLocaleString("en-AU")}`)
                : t.repay(`A$${taxable.toLocaleString("en-AU")}`, formatAud(repayment))
          : t.invalid}
      </p>
      <ShowTable>
        <SimpleTable caption={t.tableCaption(fy)} columns={t.columns} rows={rows} />
      </ShowTable>
    </figure>
  )
}

/** Medicare page visual: the levy against taxable income for a single person, with the low-income shade-in. */
function MedicareFigure({
  medicare,
  result,
  taxable,
  animate,
  fy,
  levyApplies,
  otherJob,
}: {
  medicare: IncomeTaxYear["medicare"]
  result: TakeHomeResult | undefined
  taxable: number | undefined
  animate: boolean
  fy: string
  levyApplies: boolean
  otherJob: boolean
}) {
  const locale = useLocale()
  const copy = PAY_COPY[locale]
  const t = copy.medicareFigure
  const dash = "–"
  const levy = result?.annual.medicareLevy
  const d = (n: number) => whole(money(n * 100, "AUD"))
  const rows = [
    { header: t.upTo(d(medicare.singleLower)), cells: [t.nil] },
    {
      header: `${d(medicare.singleLower + 1)} – ${d(medicare.singleUpper)}`,
      cells: [t.shade(`${medicare.shadeInBp / 100}`, d(medicare.singleLower))],
    },
    {
      header: t.over(d(medicare.singleUpper)),
      cells: [t.fullRate(percent(medicare.rateBp))],
    },
  ]
  return (
    <figure className="border-border bg-card m-0 grid content-start gap-3 rounded-xl border p-4 md:col-span-2 md:p-5">
      <figcaption className="flex items-center gap-2.5 font-semibold">
        <IconChip icon={HeartPulse} tone="warning" />
        {t.title}
      </figcaption>
      <p className="flex flex-wrap items-baseline gap-x-2">
        <b className="text-3xl font-bold tabular-nums">{levy ? whole(levy) : dash}</b>
        <span className="text-muted-foreground">{t.yearSingle}</span>
      </p>
      <MedicareShadeChart
        lower={medicare.singleLower}
        upper={medicare.singleUpper}
        shadeInBp={medicare.shadeInBp}
        rateBp={medicare.rateBp}
        taxable={levyApplies ? taxable : undefined}
        animate={animate}
      />
      <p className="text-muted-foreground -mt-2 text-[13px]">{t.axis}</p>
      <p className="text-sm">
        {taxable === undefined
          ? t.invalid
          : !levyApplies
            ? t.noLevyShown
            : otherJob
              ? t.combined(`A$${taxable.toLocaleString("en-AU")}`)
              : taxable <= medicare.singleLower
                ? t.atOrUnder(d(medicare.singleLower))
                : taxable <= medicare.singleUpper
                  ? t.reduction(percent(medicare.rateBp))
                  : t.full(d(medicare.singleUpper), percent(medicare.rateBp))}
      </p>
      <ShowTable>
        <SimpleTable caption={t.tableCaption(fy)} columns={t.columns} rows={rows} />
      </ShowTable>
    </figure>
  )
}
